import { reactive } from 'vue';

/**
 * Tiny in-memory toast queue. Pinia would work too, but a single shared
 * reactive object is enough — there's only one ToastContainer mounted
 * (in App.vue) and we only need push + dismiss.
 *
 *   import { toast } from '@/lib/toast';
 *
 *   toast.success('Saved');
 *   toast.error('Could not save');
 *   toast.info('Heads up');
 *
 * Each call returns the toast id, so callers can dismiss programmatically:
 *
 *   const id = toast.info('Saving…');
 *   await doStuff();
 *   toast.dismiss(id);
 */

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
    id: number;
    kind: ToastKind;
    message: string;
    /** Auto-dismiss after this many ms; 0 = sticky until clicked. */
    timeoutMs: number;
}

interface ToastState {
    items: Toast[];
}

const state = reactive<ToastState>({ items: [] });
let nextId = 1;

function push(kind: ToastKind, message: string, timeoutMs = 3500): number {
    const id = nextId++;
    state.items.push({ id, kind, message, timeoutMs });
    if (timeoutMs > 0) {
        setTimeout(() => dismiss(id), timeoutMs);
    }
    return id;
}

function dismiss(id: number): void {
    const i = state.items.findIndex((t) => t.id === id);
    if (i >= 0) state.items.splice(i, 1);
}

export const toast = {
    success: (msg: string, timeoutMs?: number) => push('success', msg, timeoutMs ?? 3500),
    error:   (msg: string, timeoutMs?: number) => push('error',   msg, timeoutMs ?? 6000),
    info:    (msg: string, timeoutMs?: number) => push('info',    msg, timeoutMs ?? 3500),
    dismiss,
};

/** Read-only handle for the container component. */
export function useToasts(): ToastState {
    return state;
}
